import React from 'react';
import { MapPin, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

const GPSSpoofTab = ({ gpsForm, setGpsForm, analyzeGPSData, gpsSpoofResult, getSeverityColor }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-xl border shadow-sm p-6">
      <h3 className="font-semibold text-gray-900 mb-4">GPS Spoofing Detection</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Latitude', field: 'latitude', step: '0.0001', parse: parseFloat },
          { label: 'Longitude', field: 'longitude', step: '0.0001', parse: parseFloat },
          { label: 'Satellites', field: 'satellites', parse: parseInt },
          { label: 'Signal (dBm)', field: 'signal_strength', parse: parseFloat },
          { label: 'Speed (km/h)', field: 'speed', parse: parseFloat },
          { label: 'HDOP', field: 'hdop', step: '0.1', parse: parseFloat },
        ].map(({ label, field, step, parse }) => (
          <div key={field}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input type="number" step={step} value={gpsForm[field]}
              onChange={(e) => setGpsForm({ ...gpsForm, [field]: parse(e.target.value) })}
              className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        ))}
        <div className="flex items-end col-span-2">
          <Button onClick={analyzeGPSData} className="bg-blue-600 hover:bg-blue-700">
            <MapPin className="h-4 w-4 mr-2" />Analyze GPS Data
          </Button>
        </div>
      </div>
      {gpsSpoofResult && (
        <div className={`p-4 rounded-lg border ${gpsSpoofResult.is_spoofed ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-2 mb-3">
            {gpsSpoofResult.is_spoofed ? <AlertTriangle className="h-6 w-6 text-red-600" /> : <CheckCircle className="h-6 w-6 text-green-600" />}
            <span className="text-lg font-semibold">{gpsSpoofResult.is_spoofed ? 'GPS SPOOFING DETECTED!' : 'GPS Signal Legitimate'}</span>
            <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(gpsSpoofResult.threat_level)}`}>{gpsSpoofResult.threat_level?.toUpperCase()}</span>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-2 bg-white rounded"><p className="text-lg font-bold">{gpsSpoofResult.confidence}%</p><p className="text-xs text-gray-500">Confidence</p></div>
            <div className="text-center p-2 bg-white rounded"><p className="text-lg font-bold">{gpsSpoofResult.risk_score}</p><p className="text-xs text-gray-500">Risk Score</p></div>
            <div className="text-center p-2 bg-white rounded"><p className="text-lg font-bold">{gpsSpoofResult.gps_quality?.satellites}</p><p className="text-xs text-gray-500">Satellites</p></div>
          </div>
          {gpsSpoofResult.spoof_indicators?.length > 0 && (
            <div className="space-y-2 mb-4">
              <h5 className="font-medium">Spoof Indicators:</h5>
              {gpsSpoofResult.spoof_indicators.map((ind) => (
                <div key={ind.indicator} className="p-2 bg-white rounded text-sm"><span className="font-medium">{ind.indicator}:</span> {ind.description}</div>
              ))}
            </div>
          )}
          <p className="text-sm text-blue-600">{gpsSpoofResult.recommendation}</p>
        </div>
      )}
    </div>
    {/* GPS Quality Guide */}
    <div className="bg-white rounded-xl border shadow-sm p-6">
      <h3 className="font-semibold text-gray-900 mb-4">GPS Signal Quality Guide</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { title: 'Good Signal', color: 'green', items: ['Satellites: 8-12', 'HDOP: 1.0-2.0', 'Signal: -120 to -130 dBm'] },
          { title: 'Moderate Signal', color: 'yellow', items: ['Satellites: 4-7', 'HDOP: 2.0-5.0', 'Signal: -130 to -140 dBm'] },
          { title: 'Poor Signal', color: 'orange', items: ['Satellites: <4', 'HDOP: >5.0', 'Signal: <-140 dBm'] },
          { title: 'Spoofing Signs', color: 'red', items: ['Satellites: >20', 'HDOP: <0.5', 'Signal: >-100 dBm'] },
        ].map(({ title, color, items }) => (
          <div key={title} className={`p-3 bg-${color}-50 border border-${color}-200 rounded-lg`}>
            <h4 className={`font-medium text-${color}-800`}>{title}</h4>
            <ul className={`text-xs text-${color}-700 mt-2 space-y-1`}>
              {items.map(item => <li key={item}>&#8226; {item}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default GPSSpoofTab;
